/**
 * A component responsible for rendering the list of chat messages.
 *
 * The list is virtualized with `react-virtuoso` (ELARA-854): only the on-screen messages (plus a
 * small overscan) are mounted, so opening a trip with a long history no longer mounts hundreds of
 * heavy message trees at once. Virtuoso's `followOutput` keeps the list pinned to the newest message
 * while it streams; scrolling up to read history is not yanked back down. This mirrors the mobile
 * message list (`mobile/src/ui/chat/MessageList.tsx`), which uses FlashList's
 * `maintainVisibleContentPosition` for the same effect.
 *
 * Scroll actions (the "scroll to latest" button, and jump-to-message from the shortlist) are driven
 * through the Virtuoso imperative handle registered on `MessageViewState`.
 */

import type { VirtuosoHandle } from 'react-virtuoso';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { trackChatMessageNotificationOpened } from '@elara/client-core/lib/analytics-events';
import { cn } from '@elara/client-core/lib/utils';

import {
  useAppStates,
  useChatInputState,
  useChatsState,
  useMessageViewState,
  useRouterState,
  useSessionState,
} from '#states/hooks.js';
import { EmptyChatIllustration } from '#ui/illustrations/empty-chat.js';
import { Alert, AlertDescription, Button, EmptyState, IconSizes, Loader, MaterialIcon } from '#ui/index.js';

import { MessageView } from './MessageView.jsx';

/** Centered, max-width column shared by every row so the message column stays aligned. */
const ROW_WRAPPER_CLASSES = 'mx-auto w-full max-w-3xl px-4 md:px-6 overflow-x-hidden';

/** Top/bottom breathing room for the scroll content, rendered as Virtuoso header/footer spacers. */
const ListHeaderSpacer = () => <div className="h-6 md:h-8" />;
const ListFooterSpacer = () => <div className="h-6 md:h-8" />;
const VIRTUOSO_COMPONENTS = { Header: ListHeaderSpacer, Footer: ListFooterSpacer };

/**
 * On a cold chat open, `chatsState.messages` can go `isReady` off a partial/stale local-cache
 * snapshot (any non-empty cache result flips it immediately, see `createClientQuery.ts`), with the
 * authoritative server snapshot landing moments later and growing the list. Since Virtuoso only
 * reads `initialTopMostItemIndex` once at mount, mounting on that cache preview bakes in the wrong
 * scroll target and the feed visibly jumps once the real data lands. Wait for a server-confirmed
 * snapshot before mounting — bounded by a short timeout so an offline chat still opens.
 */
const CACHE_SETTLE_TIMEOUT_MS = 500;

/**
 * Even once mounted on a settled snapshot, Virtuoso computes its `initialTopMostItemIndex` bottom
 * offset before message content (markdown, widgets, images) has actually laid out, so the "end"
 * alignment can land short of the true bottom — the last message's top is visible but its tail is
 * cut off below the fold. Snap back to bottom on every layout-height change for a short window after
 * mount, which self-corrects as real heights resolve without fighting the user's own scrolling later.
 */
const SCROLL_CORRECTION_WINDOW_MS = 500;

function useIsMessageSnapshotSettled(chatId: string | undefined, isReady: boolean, fromCache: boolean): boolean {
  const [isSettled, setIsSettled] = useState(false);

  // Reset on chat switch so the new chat's own cache/server race is tracked independently.
  useEffect(() => setIsSettled(false), [chatId]);

  useEffect(() => {
    if (isSettled || !isReady) return;
    if (!fromCache) {
      setIsSettled(true);
      return;
    }
    const timeout = setTimeout(() => setIsSettled(true), CACHE_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isSettled, isReady, fromCache, chatId]);

  return isSettled;
}

/** Query param naming the message to open the chat at (set by new-chat-message notification links). */
const LINKED_MESSAGE_PARAM = 'message';
/** How long to wait for a linked message to load before giving up and leaving the chat at the bottom. */
const LINKED_MESSAGE_TIMEOUT_MS = 5000;

/**
 * Opens the chat at the message named in `?message=` (a tapped notification), once the list is mounted
 * and past its settle-to-bottom window, then drops the param so Back and reloads don't jump again.
 */
function useScrollToLinkedMessage(canRenderMessages: boolean): void {
  const routerState = useRouterState();
  const chatsState = useChatsState();
  const messageViewState = useMessageViewState();

  const linkedId = routerState.queryParams[LINKED_MESSAGE_PARAM];
  const isLoaded = !!linkedId && chatsState.messagesList.some((message) => message.id === linkedId);

  useEffect(() => {
    if (!linkedId || !canRenderMessages) return;
    const clearParam = () => {
      const route = routerState.currentRoute;
      if (!route) return;
      const query = new URLSearchParams(route.query);
      query.delete(LINKED_MESSAGE_PARAM);
      const search = query.toString();
      routerState.navigate(`${route.path}${search ? `?${search}` : ''}${route.hash ?? ''}`, { replace: true });
    };
    if (!isLoaded) {
      const timeout = setTimeout(clearParam, LINKED_MESSAGE_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
    // After the mount-time snap-to-bottom window, or the correction would pull the list back down.
    const timeout = setTimeout(() => {
      messageViewState.scrollToMessage(linkedId, 'auto', 'start');
      const tripId = routerState.currentRoute?.params.id;
      if (tripId) trackChatMessageNotificationOpened({ tripId, chatId: chatsState.current?.id });
      clearParam();
    }, SCROLL_CORRECTION_WINDOW_MS + 100);
    return () => clearTimeout(timeout);
  }, [linkedId, isLoaded, canRenderMessages, routerState, chatsState, messageViewState]);
}

/**
 * Renders the scrollable, virtualized area containing the list of chat messages.
 */
export function ChatMessagesView() {
  const chatsState = useChatsState();
  const messageViewState = useMessageViewState();
  const chatInputState = useChatInputState();
  const sessionState = useSessionState();
  const { modelState } = useAppStates('modelState');

  // Register (and clear) the Virtuoso handle so MessageViewState can drive scroll actions imperatively.
  // Virtuoso is keyed by chat below, so this fires null→handle on chat switch, keeping the ref current.
  const isCorrectingScrollRef = useRef(false);
  const setVirtuosoRef = useCallback(
    (handle: VirtuosoHandle | null) => {
      messageViewState.setVirtuosoRef(handle);
      isCorrectingScrollRef.current = handle !== null;
      if (handle) setTimeout(() => (isCorrectingScrollRef.current = false), SCROLL_CORRECTION_WINDOW_MS);
    },
    [messageViewState],
  );
  const handleTotalListHeightChanged = useCallback(() => {
    if (isCorrectingScrollRef.current) messageViewState.scrollToBottom('auto');
  }, [messageViewState]);

  // Read signals directly in render for automatic reactivity.
  const messages = chatsState.messagesList;
  const chatId = chatsState.current?.id;
  const isReady = chatsState.isSelectionSettled && chatsState.messages.isReady;
  const isSnapshotSettled = useIsMessageSnapshotSettled(chatId, isReady, chatsState.messages.fromCache);
  // Don't mount the virtualized list until the message snapshot has settled (see hook doc above) —
  // an empty chat has no scroll-position race to settle, so it can render as soon as it's ready.
  const canRenderMessages = isReady && (messages.length === 0 || isSnapshotSettled);

  useScrollToLinkedMessage(canRenderMessages);

  const showLatestMessagesButton = canRenderMessages && messages.length > 0 && !messageViewState.isAtBottom;
  const overlayHeight = messageViewState.composerOverlayHeight;

  return (
    <div className="relative flex h-full flex-col">
      {/* Model override banner - shown for privileged users when a model override is active */}
      {sessionState.isPrivileged &&
        (modelState.selectedModel || modelState.selectedPromptVersion || modelState.skillVersionsOverride) && (
          <Alert className="mx-4 b-0 bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800 relative z-10 shrink-0">
            <MaterialIcon icon="smart_toy" size="xs" className="text-purple-600 dark:text-purple-400" />
            <AlertDescription className="text-purple-800 dark:text-purple-200">
              <span className="font-medium">Override active</span> —{' '}
              {modelState.selectedModel ? modelState.selectedModelLabel.replace(/^[^:]+:\s*/, '') : 'Default model'}
              {modelState.reasoningEffortOverride ? ` · ${modelState.reasoningEffortOverride} reasoning` : ''}
              {' · '}
              {modelState.selectedPromptVersion ? `Prompt v${modelState.selectedPromptVersion}` : 'Default prompt'}
              {Object.entries(modelState.selectedSkillVersions)
                .map(([name, version]) => ` · ${name} v${version}`)
                .join('')}
            </AlertDescription>
          </Alert>
        )}

      <div className="relative min-h-0 flex-1">
        {!canRenderMessages ? (
          // Loading state - messages are being fetched
          <div className="flex h-full items-center justify-center">
            <div className="text-center py-16">
              <Loader loading={true} />
            </div>
          </div>
        ) : messages.length === 0 ? (
          // Empty state - no messages in this chat
          <div className={cn('flex h-full items-center justify-center', ROW_WRAPPER_CLASSES)}>
            <EmptyState
              title="Where would you like to go?"
              description="Let’s start planning your next adventure."
              illustration={
                <div className="relative h-full">
                  <Loader
                    thinking={chatInputState.isTyping}
                    width={74}
                    height={74}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  />
                  <EmptyChatIllustration width={198} height={140} />
                </div>
              }
            />
          </div>
        ) : (
          <Virtuoso
            // Remount per chat so the list re-opens at the newest message (initialTopMostItemIndex is
            // only read on mount) and scroll state doesn't leak across chats within a trip.
            key={chatId}
            ref={setVirtuosoRef}
            data={messages}
            className="h-full overflow-x-hidden"
            // Open scrolled all the way to the newest message. A plain number here would align that
            // item's TOP to the viewport top (Virtuoso's default), not scroll to the bottom — align:
            // 'end' anchors its bottom edge to the viewport bottom, matching scrollToBottom() below.
            initialTopMostItemIndex={{ index: messages.length - 1, align: 'end' }}
            // Stay pinned to the bottom while streaming ONLY when already at the bottom; a user who has
            // scrolled up to read history is never yanked down.
            followOutput={(isAtBottom) => (isAtBottom ? 'smooth' : false)}
            atBottomThreshold={120}
            atBottomStateChange={(atBottom) => messageViewState.setIsAtBottom(atBottom)}
            totalListHeightChanged={handleTotalListHeightChanged}
            computeItemKey={(_, message) => message.id}
            itemContent={(_, message) => (
              <div className={ROW_WRAPPER_CLASSES}>
                <div className="pb-8">
                  <MessageView msg={message} />
                </div>
              </div>
            )}
            components={VIRTUOSO_COMPONENTS}
          />
        )}

        {showLatestMessagesButton && (
          <div
            className="z-15 absolute left-1/2 -translate-x-1/2"
            // 24px (`bottom-6`) normally; lifted 12px clear of anything floating above the composer.
            style={{ bottom: overlayHeight > 0 ? overlayHeight + 12 : 24 }}
          >
            <Button
              size="icon"
              variant="icon"
              className="shadow-sm bg-background"
              onClick={() => messageViewState.scrollToBottom()}
            >
              <MaterialIcon icon="arrow_downward" size={IconSizes.sm} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
